import {
  Button,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Toolbar,
} from '@dls/react-core';
import React, { useState } from 'react';
import { em } from '@dls/react-theme';
import { Typography } from '@dls/react-mui-typography';
import {
  EnhancedTableHead,
  HeadCell,
  Order,
  sortedTableStyles,
} from '../../components/sorted-table/sorted-table-head';
import { getComparator, stableSort } from '../../components/sorted-table/sorting-utils';
import { Link } from '@dls/react-mui-link';
import styled from 'styled-components';
import { ColorDLS } from '../../styling/color';
import { useNavigate } from 'react-router-dom';
import { RoutePaths } from '../../routes/main-routes';
import { PageWrapper } from '../../components/styled/styles';
import { RemoveAlgorithmDialog } from '../../components/dialogs/remove-algorithm-dialog';
import { AlgorithmWithId, getAlgorithmsAxios } from '../../api/studykit';
import useRequest from '../../api/useRequest';

const FakeLink = styled(Link)`
  cursor: Pointer;

  :visited {
    color: ${ColorDLS.Blue45};
  }
`;

const StyledTableButton = styled(Button)`
  &&& {
    padding: 0;
    justify-content: start;
  }
`;

const headCells: HeadCell<AlgorithmWithId>[] = [
  { type: 'sortable', id: 'name', numeric: false, disablePadding: true, label: 'Name' },
  { type: 'sortable', id: 'versions', numeric: false, disablePadding: true, label: 'Versions' },
  { type: 'sortable', id: 'repository', numeric: false, disablePadding: true, label: 'Repository' },
  { type: 'other', id: 'actions', disablePadding: true, label: 'Actions' },
];

function Algorithms() {
  const classes = sortedTableStyles();
  const navigate = useNavigate();

  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof AlgorithmWithId>('name');
  const [selectedAlgorithm, setSelectedAlgorithm] = useState<AlgorithmWithId | undefined>(
    undefined,
  );
  const [openRemoveAlgorithmDialog, setOpenRemoveAlgorithmDialog] = useState(false);

  const { data: algorithms, mutate } = useRequest(getAlgorithmsAxios());

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof AlgorithmWithId) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <>
      {selectedAlgorithm && (
        <RemoveAlgorithmDialog
          open={openRemoveAlgorithmDialog}
          algorithm={selectedAlgorithm}
          onClose={() => setOpenRemoveAlgorithmDialog(false)}
          mutate={mutate}
        />
      )}
      <PageWrapper>
        <Paper className={'dlsTable'}>
          <Toolbar className={'dlsTableHeader'}>
            <Typography
              weight="bold"
              style={{
                flex: '1 1 100%',
                paddingRight: `${em(24)}`,
                fontSize: `${em(18)}`,
                marginBottom: 'none',
              }}
              id="tableTitle"
            >
              Algorithms
            </Typography>
            <Typography style={{ whiteSpace: 'nowrap', paddingRight: `${em(24)}` }}>
              <FakeLink
                className={'noUnderLineLink'}
                onClick={() => navigate(`/${RoutePaths.Settings}/${RoutePaths.AddAlgorithm}`)}
                data-testid="algorithm-link"
              >
                New Algorithm
              </FakeLink>
            </Typography>
          </Toolbar>
          <TableContainer style={{ width: 'unset' }}>
            {!algorithms ? (
              <LinearProgress variant="indeterminate" />
            ) : (
              <Table aria-label="simple table " data-testid="algorithm-table">
                <EnhancedTableHead
                  classes={classes}
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                  headCells={headCells}
                />
                <TableBody>
                  {stableSort(algorithms, getComparator(order, orderBy)).map((row, index) => {
                    return (
                      <TableRow key={row.name} data-testid={'row-' + index}>
                        <TableCell scope="row" data-testid={'name-' + index}>
                          {row.name}
                        </TableCell>
                        <TableCell data-testid={'version-' + index}>
                          {row.versions.join(', ')}
                        </TableCell>
                        <TableCell data-testid={'repository-' + index}>{row.repository}</TableCell>
                        <TableCell>
                          <StyledTableButton
                            dls_variant="quietEmphasis"
                            onClick={() => {
                              setSelectedAlgorithm(row);
                              setOpenRemoveAlgorithmDialog(true);
                            }}
                          >
                            Delete
                          </StyledTableButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </Paper>
      </PageWrapper>
    </>
  );
}

export default Algorithms;
